import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Column} from 'asax-common';
import {ColumnsService} from '../columns.service';
import {AsaxBookmarkingService} from 'asax-bookmarking';
import {tap} from "rxjs/operators";
import {DragulaService} from "ng2-dragula";

type SortDir = 'asc' | 'desc';

const sortColumnsBySortDir = (columns: Column[], sortDir: SortDir): Column[] => {
  const columnsCantBeSorted = columns.filter(column => column.frozenLeft);
  const columnsCanBeSorted = columns.filter(column => !column.frozenLeft);

  const columnsSorted = columnsCanBeSorted.sort((a: Column, b: Column) =>
    sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
  );

  return [...columnsCantBeSorted, ...columnsSorted];
};

@Component({
  selector: 'asax-result-table-columns-picker-container',
  templateUrl: './columns-picker-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .first-child {
      border-top-left-radius: inherit;
      border-top-right-radius: inherit;
      border-top-width: 1px;
    }
  `]
})
export class ColumnsPickerContainerComponent implements OnInit {
  @Input() id: string;

  @Output() outResetColumns = new EventEmitter<any>();

  columns: Column[];

  constructor(private readonly columnsService: ColumnsService,
              private readonly asaxBookmarkingService: AsaxBookmarkingService,
              private readonly modalService: NgbModal,
              private dragulaService: DragulaService) {
  }

  ngOnInit(): void {
    this.columnsService.getColumns().pipe(
      tap(columns => this.columns = columns)
    ).subscribe();

    this.dragulaService.dropModel(`${this.id}ColumnsPicker`).pipe(
      tap(({targetModel}) => setTimeout(() => this.columnsService.setColumns(targetModel)))
    ).subscribe();
  }

  openModalColumns(content): void {
    this.modalService.open(content, {centered: true, scrollable: true, size: 'md'});
  }

  toggleColumn(column: Column): void {
    this.columnsService.upsertColumn({...column, selected: !column.selected});
  }

  hideColumns(): void {
    const columnsCantBeHidden = this.columnsService.getColumnsValue().filter(column => column.frozenLeft);
    const columnsCanBeHidden = this.columnsService.getColumnsValue().filter(column => !column.frozenLeft);
    const columnsHidden = columnsCanBeHidden.map(column => ({...column, selected: false}));
    this.columnsService.setColumns([...columnsCantBeHidden, ...columnsHidden]);
  }

  showColumns(): void {
    const columns = this.columnsService.getColumnsValue().map(column => ({...column, selected: true}));
    this.columnsService.setColumns([...columns]);
  }

  resetColumns(): void {
    this.columnsService.setColumns(this.columnsService.getDefaultColumns());
  }

  orderAscColumns(): void {
    const columns = sortColumnsBySortDir(this.columnsService.getColumnsValue(), 'asc');
    this.columnsService.setColumns([...columns]);
  }

  orderDescColumns(): void {
    const columns = sortColumnsBySortDir(this.columnsService.getColumnsValue(), 'desc');
    this.columnsService.setColumns([...columns]);
  }
}
